import React, { useState, useEffect } from 'react';
import { FaSearch, FaPlus, FaSave, FaTimes, FaEdit, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import ConfirmModal from '../components/ConfirmModal';


const { API_BASE_URL } = config;

function numeroParaReais(valor) {
  if (isNaN(valor) || valor === null) return 'R$ 0,00';
  return valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/**
 * Ordena lista de produtos para exibição na Ordem de Serviço:
 * 1º - Produtos com prioridade === true, em ordem alfabética pelo nome
 * 2º - Demais produtos (prioridade === false, null ou undefined), em ordem alfabética pelo nome
 *
 * Exportada para ser reutilizada em outros componentes (ex: tela de Nova Ordem de Serviço).
 */
export function ordenarProdutosPorPrioridade(produtos) {
  if (!produtos || produtos.length === 0) return [];

  const normalizarNome = (nome) => (nome || '').toLowerCase().trim();

  const prioritarios = produtos
    .filter(p => p.prioridade === true)
    .sort((a, b) => normalizarNome(a.nome).localeCompare(normalizarNome(b.nome), 'pt-BR'));

  const demais = produtos
    .filter(p => p.prioridade !== true)
    .sort((a, b) => normalizarNome(a.nome).localeCompare(normalizarNome(b.nome), 'pt-BR'));

  return [...prioritarios, ...demais];
}

const CadastroServico = () => {
  const [servico, setServico] = useState({
    idServico: null,
    descricao: '',
    valor: null,
    servicoPassivos: []
  });

  const [servicosPassivosDisponiveis, setServicosPassivosDisponiveis] = useState([]);
  const [modalPesquisaServicoAberta, setModalPesquisaServicoAberta] = useState(false);
  const [modalServicoPassivoAberta, setModalServicoPassivoAberta] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { addToast } = useToast();
  const [errors, setErrors] = useState({
    descricao: '',
    valor: ''
  });

  // Carrega serviços passivos disponíveis
  useEffect(() => {
    const carregarServicosPassivos = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('jwtToken');
        const response = await axios.get(`${API_BASE_URL}/servicoPassivos`, {
          headers: {
            'Authorization': `Bearer ${token}`
          },
        });
        setServicosPassivosDisponiveis(response.data);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar serviços passivos:', err);
        setError(err.response?.data?.message || 'Erro ao carregar serviços passivos');
      } finally {
        setLoading(false);
      }
    };

    carregarServicosPassivos();
  }, []);

  const validateField = (name, value) => {
    let error = '';
    if (!value && value !== 0) {
      error = 'Este campo é obrigatório';
    } else if (name === 'valor' && (value <= 0 || isNaN(value))) {
      error = 'Valor deve ser maior que zero';
    }
    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  const handlePesquisarServico = () => {
    setModalPesquisaServicoAberta(true);
  };

  const handleSelecionarServico = (servicoSelecionado) => {
    setServico({
      idServico: servicoSelecionado.idServico,
      descricao: servicoSelecionado.descricao,
      valor: servicoSelecionado.valor,
      servicoPassivos: servicoSelecionado.servicoPassivos || []
    });
    setModalPesquisaServicoAberta(false);
  };

  const handleAdicionarServicoPassivo = () => {
    setModalServicoPassivoAberta(true);
  };

  const toggleServicoPassivoSelecionado = (idServicoPassivo) => {
    setServico(prev => {
      const exists = prev.servicoPassivos.some(sp => sp.idServicoPassivo === idServicoPassivo);

      if (exists) {
        return {
          ...prev,
          servicoPassivos: prev.servicoPassivos.filter(sp => sp.idServicoPassivo !== idServicoPassivo)
        };
      } else {
        const passivo = servicosPassivosDisponiveis.find(sp => sp.idServicoPassivo === idServicoPassivo);
        return {
          ...prev,
          servicoPassivos: [...prev.servicoPassivos, passivo]
        };
      }
    });
  };

  const handleSalvar = async () => {
    const isValidDescricao = validateField('descricao', servico.descricao);
    const isValidValor = validateField('valor', servico.valor);
    if (!isValidDescricao || !isValidValor) {
      addToast('warning', 'Preencha todos os campos obrigatórios corretamente!');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const servicoParaEnviar = {
        descricao: servico.descricao,
        valor: servico.valor,
        servicoPassivosIds: servico.servicoPassivos.map(sp => sp.idServicoPassivo)
      };
  
      let response;
      const token = localStorage.getItem('jwtToken');
     
      if (servico.idServico) {
        response = await axios.put(
          `${API_BASE_URL}/servicos/${servico.idServico}`,
          servicoParaEnviar,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/servicos`,
          servicoParaEnviar,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }
      addToast('success', servico.idServico ? 'Serviço atualizado com sucesso!' : 'Serviço criado com sucesso!');
      limparFormulario();
    } catch (err) {
      console.error('Erro ao salvar serviço:', err);
      setError(err.response?.data?.message || 'Erro ao salvar serviço');
    } finally {
      setLoading(false);
    }
  };

  const limparFormulario = () => {
    setServico({
      idServico: null,
      descricao: '',
      valor: null,
      servicoPassivos: []
    });
    setError(null);
    setErrors({
      descricao: '',
      valor: ''
    });
  };

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <div style={styles.header}>
          <h1 style={styles.title}>Cadastro de Serviço</h1>
        </div>

        {error && (
          <div style={styles.errorMessage}>
            {error}
          </div>
        )}

        <div style={styles.formContainer}>
          {/* Botão de pesquisa */}
          <div style={styles.formRow}>
            <div style={styles.inputGroup}>
              <button
                onClick={handlePesquisarServico}
                style={styles.searchButton}
                disabled={loading}
              >
                <FaSearch /> Pesquisar Serviço
              </button>
            </div>
          </div>

          {/* Campos do serviço */}
          <div style={styles.formRow}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Descrição*</label>
              <input
                type="text"
                value={servico.descricao}
                onChange={(e) => {
                  setServico({ ...servico, descricao: e.target.value });
                  validateField('descricao', e.target.value);
                }}
                placeholder="Descrição do serviço"
                style={{ ...styles.input, ...(errors.descricao && styles.inputError) }}
                disabled={loading}
                onBlur={() => validateField('descricao', servico.descricao)}
              />
              {errors.descricao && <span style={styles.errorText}>{errors.descricao}</span>}
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Valor*</label>
              <NumericFormat
                value={servico.valor}
                onValueChange={(values) => {
                  setServico({ ...servico, valor: values.floatValue || 0 });
                  validateField('valor', values.floatValue);
                }}
                thousandSeparator="."
                decimalSeparator=","
                placeholder="R$ 0,00"
                decimalScale={2}
                fixedDecimalScale
                style={{ ...styles.input, ...(errors.valor && styles.inputError) }}
                disabled={loading}
                onBlur={() => validateField('valor', servico.valor)}
              />
              {errors.valor && <span style={styles.errorText}>{errors.valor}</span>}
            </div>
          </div>

          {/* Botão para adicionar serviço passivo */}
          <div style={styles.formRow}>
            <button
              onClick={handleAdicionarServicoPassivo}
              style={styles.secondaryButton}
              disabled={loading}
            >
              <FaPlus /> Adicionar/Editar Serviço Passivo
            </button>
          </div>

          {/* Lista de serviços passivos disponíveis */}
          <div style={styles.servicosPassivosContainer}>
            <h3 style={styles.subtitle}>Serviços Passivos Disponíveis</h3>
            {servicosPassivosDisponiveis.length > 0 ? (
              <div>
                {servicosPassivosDisponiveis?.map(passivo => (
                  <div key={passivo.idServicoPassivo} style={styles.servicoItem}>
                    <label style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={servico.servicoPassivos.some(sp => sp.idServicoPassivo === passivo.idServicoPassivo)}
                        onChange={() => toggleServicoPassivoSelecionado(passivo.idServicoPassivo)}
                        style={styles.checkbox}
                        disabled={loading}
                      />
                      <span style={styles.servicoDescricao}>{passivo.descricao}</span>
                      <span style={styles.servicoValor}>{passivo?.valorDigitavel ? 'Digitável' : numeroParaReais(passivo?.valor)}</span>
                    </label>
                  </div>
                ))}
              </div>
            ) : (
              <p style={styles.noResults}>Nenhum serviço passivo cadastrado</p>
            )}
          </div>

          {/* Botões de ação */}
          <div style={styles.formActions}>
            <button
              onClick={limparFormulario}
              style={styles.cancelButton}
              disabled={loading}
            >
              <FaTimes /> Limpar
            </button>
            <button
              onClick={handleSalvar}
              style={styles.primaryButton}
              disabled={!servico.descricao || !servico.valor || loading}
            >
              {loading ? 'Salvando...' : (
                <>
                  <FaSave /> {servico.idServico ? 'Atualizar' : 'Salvar'}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Modal para cadastro/edição de serviço passivo */}
        {modalServicoPassivoAberta && (
          <ModalServicoPassivo
            onClose={() => setModalServicoPassivoAberta(false)}
            servicosExistentes={servicosPassivosDisponiveis}
            setServicosExistentes={setServicosPassivosDisponiveis}
            loading={loading}
            setLoading={setLoading}
            setError={setError}
          />
        )}

        {/* Modal para pesquisa de serviço */}
        {modalPesquisaServicoAberta && (
          <ModalPesquisaServico
            onClose={() => setModalPesquisaServicoAberta(false)}
            onSelect={handleSelecionarServico}
            loading={loading}
            setLoading={setLoading}
            setError={setError}
          />
        )}
      </div>
    </div>
  );
};

const ModalServicoPassivo = ({ 
  onClose, 
  servicosExistentes, 
  setServicosExistentes,
  loading, 
  setLoading,
  setError 
}) => {
  const [servicoPassivoEditando, setServicoPassivoEditando] = useState(null);
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [disabledValorPassivo, setDisabledValorPassivo] = useState(false);
  const [valorDigitavel, setValorDigitavel] = useState(false);
  const [errors, setErrors] = useState({
    descricao: '',
    valor: ''
  });
  const { addToast } = useToast();

  const alterarValorCheckValorDigitavel = (value) => {
    setValor(0);
    if(value){
      setDisabledValorPassivo(true)
    }else{
      setDisabledValorPassivo(false)
    }
    setValorDigitavel(value)
  }

  useEffect(() => {
    if (servicoPassivoEditando) {
      setDescricao(servicoPassivoEditando.descricao);
      setValor(servicoPassivoEditando.valor || 0);
      setValorDigitavel(servicoPassivoEditando.valorDigitavel)
    } else {
      setDescricao('');
      setValor(0);
      setValorDigitavel(false);
    }
  }, [servicoPassivoEditando]);

  const validateField = (name, value) => {
    let error = '';
    if (!value && value === '') {
      error = 'Este campo é obrigatório';
    }
    setErrors(prev => ({ ...prev, [name]: error }));
    return !error;
  };

  const handleSalvar = async () => {
    const valorNumerico = typeof valor === 'string' ? parseFloat(valor.replace(/\./g, '').replace(',', '.')) : valor;
    const isValidDescricao = validateField('descricao', descricao);
    const isValidValor = validateField('valor', valorNumerico);
    if (!isValidDescricao || !isValidValor) {
      addToast('warning', 'Preencha todos os campos obrigatórios corretamente!');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('jwtToken');
      
      const dadosServicoPassivo = {
        descricao,
        valor: valorNumerico,
        valorDigitavel
      };

      let response;
      if (servicoPassivoEditando) {
        response = await axios.put(
          `${API_BASE_URL}/servicoPassivos/${servicoPassivoEditando.idServicoPassivo}`,
          dadosServicoPassivo,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        setServicosExistentes(prev => 
          prev.map(sp => 
            sp.idServicoPassivo === servicoPassivoEditando.idServicoPassivo ? response.data : sp
          )
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/servicoPassivos`,
          dadosServicoPassivo,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        setServicosExistentes([...servicosExistentes, response.data]);
      }

      setDescricao('');
      setValor('');
      setValorDigitavel(false);
      setServicoPassivoEditando(null);
    } catch (err) {
      console.error('Erro ao salvar serviço passivo:', err);
      setError(err.response?.data?.message || 'Erro ao salvar serviço passivo');
    } finally {
      setLoading(false);
    }
  };

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    message: '',
    onConfirm: () => { },
    title: 'Confirmação'
  });

  const showConfirm = (message, onConfirm, title = 'Confirmação') => {
    setConfirmModal({
      isOpen: true,
      message,
      onConfirm,
      title
    });
  };

  const closeConfirm = () => {
    setConfirmModal({
      isOpen: false,
      message: '',
      onConfirm: () => { },
      title: 'Confirmação'
    });
  };

  const handleExcluir = async (idServicoPassivo) => {
    showConfirm(
      'Tem certeza que deseja excluir este serviço passivo?',
      async () => {
        try {
          setLoading(true);
          const token = localStorage.getItem('jwtToken');
          await axios.delete(`${API_BASE_URL}/servicoPassivos/${idServicoPassivo}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
    
          setServicosExistentes(prev => 
            prev.filter(sp => sp.idServicoPassivo !== idServicoPassivo)
          );
    
          addToast('success', 'Serviço passivo excluído com sucesso!');
        } catch (err) {
          console.error('Erro ao excluir serviço passivo:', err);
          setError(err.response?.data?.message || 'Erro ao excluir serviço passivo');
        } finally {
          setLoading(false);
        }
      }, 'Excluir serviço passivo'
    );
  };

  const handleEditar = (servicoPassivo) => {
    setServicoPassivoEditando(servicoPassivo);
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            {servicoPassivoEditando ? 'Editar Serviço Passivo' : 'Cadastrar Serviço Passivo'}
          </h2>
          <button onClick={onClose} style={styles.modalClose} disabled={loading}>
            &times;
          </button>
        </div>
        <div style={styles.modalContent}>
          <br/>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Descrição*</label>
            <input
              type="text"
              value={descricao}
              onChange={(e) => {
                setDescricao(e.target.value);
                validateField('descricao', e.target.value);
              }}
              placeholder="Descrição do serviço passivo"
              style={{ ...styles.input, ...(errors.descricao && styles.inputError) }}
              disabled={loading}
              onBlur={() => validateField('descricao', descricao)}
            />
            {errors.descricao && <span style={styles.errorText}>{errors.descricao}</span>}
          </div>
          <br/>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Valor*</label>
            <NumericFormat
              value={valor}
              onValueChange={(values) => {
                setValor(values.floatValue || 0);
              }}
              thousandSeparator="."
              decimalSeparator=","
              placeholder="R$ 0,00"
              decimalScale={2}
              fixedDecimalScale
              style={{ ...styles.input, ...(errors.valor && styles.inputError) }}
              disabled={disabledValorPassivo}
            />
            {errors.valor && <span style={styles.errorText}>{errors.valor}</span>}
          </div>
          <br/>
          <div style={styles.inputGroup}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={valorDigitavel}
                onChange={(e) => alterarValorCheckValorDigitavel(e.target.checked)}
                style={styles.checkbox}
                disabled={loading}
              />
              <span style={styles.servicoDescricao}>Possui valor digitável</span>
            </div>
          </div>

          <div style={styles.servicosExistentes}>
            <h3 style={styles.subtitle}>Serviços Cadastrados</h3>
            {servicosExistentes.length > 0 ? (
              <ul style={styles.servicosList}>
                {servicosExistentes.map(servico => (
                  <li 
                    key={servico.idServicoPassivo} 
                    style={styles.servicoItemModal}
                    onClick={() => handleEditar(servico)}
                  >
                    <div style={styles.servicoInfoModal}>
                      <span style={styles.servicoDescricao}>{servico.descricao}</span>
                      <span style={styles.servicoValor}>{servico?.valorDigitavel ? 'Digitável' : numeroParaReais(servico.valor)}</span>
                    </div>
                    <div style={styles.servicoPassivoActions}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditar(servico);
                        }}
                        style={styles.editButton}
                        disabled={loading}
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExcluir(servico.idServicoPassivo);
                        }}
                        style={styles.deleteButton}
                        disabled={loading}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={styles.noResults}>Nenhum serviço cadastrado</p>
            )}
          </div>
        </div>
        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.secondaryButton} disabled={loading}>
            Cancelar
          </button>
          <button
            onClick={handleSalvar}
            style={styles.primaryButton}
            disabled={!descricao || !valor || loading}
          >
            {loading ? 'Salvando...' : (
              <>
                <FaSave /> {servicoPassivoEditando ? 'Atualizar' : 'Salvar'}
              </>
            )}
          </button>
        </div>
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={closeConfirm}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText="Excluir"
          cancelText="Cancelar"
        />
      </div>
    </div>
  );
};

const ModalPesquisaServico = ({ onClose, onSelect, loading, setLoading, setError }) => {
  const [termoPesquisa, setTermoPesquisa] = useState('');
  const [servicosEncontrados, setServicosEncontrados] = useState([]);

  const pesquisarServicos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('jwtToken');
      const response = await axios.get(`${API_BASE_URL}/servicos`, {
        params: { descricao: termoPesquisa },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setServicosEncontrados(response.data);
      setError(null);
    } catch (err) {
      console.error('Erro ao pesquisar serviços:', err);
      setError(err.response?.data?.message || 'Erro ao pesquisar serviços');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Pesquisar Serviço</h2>
          <button onClick={onClose} style={styles.modalClose} disabled={loading}>
            &times;
          </button>
        </div>
        <div style={styles.modalContent}>
          <div style={styles.searchBox}>
            <input
              type="text"
              value={termoPesquisa}
              onChange={(e) => setTermoPesquisa(e.target.value)}
              placeholder="Digite a descrição do serviço"
              style={styles.searchInput}
              disabled={loading}
            />
            <button
              onClick={pesquisarServicos}
              style={styles.searchButton}
              disabled={loading}
            >
              <FaSearch /> Pesquisar
            </button>
          </div>

          {loading ? (
            <div style={styles.loading}>Carregando...</div>
          ) : (
            <div style={styles.resultsContainer}>
              {servicosEncontrados.length > 0 ? (
                servicosEncontrados.map(servico => (
                  <div
                    key={servico.idServico}
                    style={styles.servicoItemSelectable}
                    onClick={() => onSelect(servico)}
                  >
                    <div style={styles.servicoInfo}>
                      <span style={styles.servicoDescricao}>{servico.descricao}</span>
                      <span style={styles.servicoValor}>{numeroParaReais(servico.valor)}</span>
                    </div>
                    {servico.servicoPassivos?.length > 0 && (
                      <div style={styles.servicoPassivos}>
                        <span style={styles.passivosLabel}>Passivos:</span>
                        {servico.servicoPassivos.map(passivo => (
                          <span key={passivo.idServicoPassivo} style={styles.passivoTag}>
                            {passivo.descricao}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p style={styles.noResults}>Nenhum serviço encontrado</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    color: '#333',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
  },
  content: {
    padding: '24px',
    maxWidth: '1000px',
    margin: '0 auto',
    width: '100%'
  },
  header: {
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    margin: '0',
    color: '#333'
  },
  errorMessage: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '20px'
  },
  subtitle: {
    fontSize: '18px',
    margin: '0 0 16px 0',
    color: '#444'
  },
  formContainer: {
    backgroundColor: 'white',
    padding: '25px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    marginBottom: '30px'
  },
  formRow: {
    display: 'flex',
    gap: '30px',
    marginBottom: '25px',
    flexWrap: 'wrap',
    alignItems: 'flex-end'
  },
  inputGroup: {
    flex: '1',
    minWidth: '200px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    fontSize: '14px',
    color: '#34495e'
  },
  input: {
    width: '95%',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '15px',
    transition: 'border 0.3s'
  },
  inputError: {
    borderColor: '#ff4444'
  },
  errorText: {
    color: '#ff4444',
    fontSize: '12px',
    marginTop: '4px',
    display: 'block'
  },
  searchButton: {
    padding: '12px 16px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.3s'
  },
  servicosPassivosContainer: {
    marginTop: '30px',
    paddingTop: '20px',
    borderTop: '1px solid #eee'
  },
  servicosExistentes: {
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: '1px solid #eee'
  },
  servicosList: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  servicoItem: {
    padding: '12px 0',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center'
  },
  servicoItemModal: {
    padding: '12px',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer'
  },
  servicoItemSelectable: {
    padding: '12px',
    borderBottom: '1px solid #f0f0f0',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  servicoDescricao: {
    flex: '1'
  },
  servicoValor: {
    color: '#2ecc71',
    fontWeight: '600'
  },
  servicoInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  servicoInfoModal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: 1
  },
  servicoPassivos: {
    marginTop: '8px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    alignItems: 'center'
  },
  passivosLabel: {
    fontSize: '12px',
    color: '#7f8c8d'
  },
  passivoTag: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px'
  },
  servicoPassivoActions: {
    display: 'flex',
    gap: '8px',
    marginLeft: '15px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    cursor: 'pointer'
  },
  checkbox: {
    marginRight: '10px'
  },
  noResults: {
    color: '#95a5a6',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '20px 0'
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '15px',
    marginTop: '30px'
  },
  primaryButton: {
    padding: '12px 20px',
    backgroundColor: '#2ecc71',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.3s'
  },
  secondaryButton: {
    padding: '12px 20px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.3s'
  },
  cancelButton: {
    padding: '12px 20px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'background-color 0.3s'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: '1px solid #eee'
  },
  modalTitle: {
    fontSize: '20px',
    margin: 0,
    color: '#333'
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#777'
  },
  modalContent: {
    padding: '20px'
  },
  modalFooter: {
    padding: '20px',
    borderTop: '1px solid #eee',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '15px'
  },
  searchBox: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px'
  },
  searchInput: {
    flex: '1',
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '15px'
  },
  resultsContainer: {
    maxHeight: '400px',
    overflowY: 'auto'
  },
  editButton: {
    padding: '8px 12px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    transition: 'background-color 0.3s'
  },
  deleteButton: {
    padding: '8px 12px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    transition: 'background-color 0.3s'
  },
  loading: {
    textAlign: 'center',
    padding: '20px',
    color: '#7f8c8d'
  }
};

export default CadastroServico;
